import React, { useEffect, useState, useCallback } from "react"; // Added useCallback
import * as api from "../../api/index";
import { jwtDecode } from "jwt-decode";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { FiHome, FiPlusSquare,FiCreditCard, FiMail, FiUser, FiSearch, FiMessageSquare } from "react-icons/fi";

const Conversations = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  // --- ADDED PAGINATION STATE (DO NOT REMOVE) ---
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  let userId = null;
  let typeofuser = null; 
  try {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode(token);
      userId = decoded._id || decoded.id;
      typeofuser = decoded.typeofuser;
    }
  } catch (err) {
    // Keep error handling as is
  }

  // --- UPDATED FETCH LOGIC FOR 20 ITEMS (STRICTLY ADDITIVE) ---
  const fetchConvos = useCallback(async (pageNum = 1, shouldRefresh = false) => {
    if (!userId) return;
    if (pageNum > 1 && !hasMore) return; // Stop if no more data

    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      setErrorMsg("");
      
      // Request 20 items from your API
      const res = await api.getConversations(userId, pageNum, 20);
      const newConvos = res.data.conversations || [];

      if (shouldRefresh || pageNum === 1) {
        setConversations(newConvos);
        setHasMore(newConvos.length === 20);
        setPage(1);
      } else {
        setConversations((prev) => [...prev, ...newConvos]);
        setHasMore(newConvos.length === 20);
      }
    } catch (err) {
      setErrorMsg("Failed to load chats.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [userId, hasMore]);

  useEffect(() => {
    fetchConvos(1, true);
  }, [userId]); // Removed fetchConvos from dependency to prevent loops, using userId trigger

  // --- ADDED LOAD MORE HANDLER ---
  const handleLoadMore = () => {
    if (!loading && !loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchConvos(nextPage);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-24">
      {/* 🔝 Sticky Header - UNCHANGED */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition text-gray-700 flex items-center gap-1"
        >
          <IoMdArrowBack size={24} />
          <span className="text-[10px] font-black uppercase tracking-widest">Back</span>
        </button>
        <h1 className="text-lg font-black text-gray-900 tracking-tight">Messages</h1>
        <button className="p-2 text-gray-400 hover:text-gray-900 transition">
          <FiSearch size={20} />
        </button>
      </div>

      <div className="max-w-xl mx-auto px-6 pt-8">
        {/* 💬 Section Title & Count - UNCHANGED */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-3xl font-black text-gray-900 tracking-tighter">My Chats</h2>
            <p className="text-[10px] font-black text-orange-600 uppercase tracking-[0.2em] mt-1">
              {conversations.length} Active Conversations
            </p>
          </div>
          <div className="w-12 h-12 bg-gray-900 rounded-2xl flex items-center justify-center text-white shadow-lg">
            <FiMessageSquare size={20} />
          </div>
        </div>

        {/* 📋 Conversations List - UNCHANGED */}
        <div className="space-y-3">
          {loading && conversations.length === 0 ? (
            <div className="flex flex-col items-center py-20 gap-4">
              <div className="w-8 h-8 border-4 border-gray-200 border-t-orange-600 rounded-full animate-spin"></div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Loading Chats...</p>
            </div>
          ) : errorMsg ? (
            <div className="bg-red-50 p-6 rounded-[2rem] border border-red-100 text-center">
              <p className="text-red-600 font-black text-xs uppercase tracking-tight">{errorMsg}</p>
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center py-24 opacity-30">
              <FiMail size={64} className="mb-4 text-gray-300" />
              <p className="font-black text-gray-400 uppercase tracking-widest text-sm">No chats yet</p>
            </div>
          ) : (
            <>
              {conversations.map((c) => {
                const otherUser = c.members.find((m) => m._id !== userId);
                return (
                  <div
                    key={c._id}
                    onClick={() => navigate(`/chat/${c._id}`, { state: { otherUser } })}
                    className="group bg-white p-4 rounded-[2rem] border border-gray-100 shadow-sm hover:shadow-xl hover:shadow-gray-200/50 transition-all cursor-pointer flex items-center justify-between active:scale-[0.98]"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="relative">
                        <img
                          src={otherUser?.profileImage || "https://via.placeholder.com/150"} 
                          alt={otherUser?.Name}
                          className="w-14 h-14 rounded-[1.2rem] object-cover border-2 border-white shadow-sm transition-transform group-hover:scale-105"
                        />
                        {c.unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-600 border-4 border-white rounded-full"></span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-black text-gray-900 text-sm uppercase tracking-tight truncate">
                          {otherUser?.Name || "User"}
                        </p>
                        <p className={`text-xs truncate max-w-[180px] mt-0.5 ${c.unreadCount > 0 ? "font-bold text-gray-900" : "font-medium text-gray-500"}`}>
                          {c.lastMessage ? (c.lastMessage.text || "[File]") : "Start a conversation"}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2 ml-4">
                      <span className="text-[9px] font-black text-gray-300 uppercase whitespace-nowrap">
                        {c.lastMessage?.createdAt
                          ? new Date(c.lastMessage.createdAt).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : ""}
                      </span>
                      {c.unreadCount > 0 && (
                        <span className="bg-orange-600 text-white text-[9px] font-black px-2 py-1 rounded-lg uppercase tracking-tighter">
                          {c.unreadCount} New
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* 🔄 LOAD MORE BUTTON (Additive) */}
              {hasMore && (
                <div className="flex justify-center py-6">
                  <button 
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="text-[10px] font-black text-orange-600 uppercase tracking-widest border-2 border-orange-100 px-6 py-2 rounded-full hover:bg-orange-50 transition"
                  >
                    {loadingMore ? "Loading..." : "Load More Chats"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* 🧭 Bottom Navigation Bar - UNCHANGED */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-t border-gray-100 md:flex justify-around items-center py-4 hidden z-50 shadow-[0_-10px_30px_rgba(0,0,0,0.03)]">
        <button onClick={() => navigate("/")} className="flex flex-col items-center gap-1 text-gray-400 hover:text-orange-600 transition-all">
          <FiHome size={22} />
          <span className="text-[9px] font-black uppercase tracking-widest">Home</span>
        </button>

        <button onClick={() => navigate("/sellproduct")} className="flex flex-col items-center gap-1 text-gray-400 hover:text-orange-600 transition-all">
          <FiPlusSquare size={22} />
          <span className="text-[9px] font-black uppercase tracking-widest">Post</span>
        </button>

         
                       <button onClick={() => navigate(`/balancepage/${userId}`)} className="flex flex-col items-center gap-1 text-gray-400 hover:text-orange-600 transition-all">
                            <FiCreditCard size={22} />
                            <span className="text-[9px] font-black uppercase tracking-widest">Wallet</span>
                          </button>

        <button onClick={() => navigate("/conversations")} className="flex flex-col items-center gap-1 text-orange-600 transition-all">
          <FiMail size={22} />
          <span className="text-[9px] font-black uppercase tracking-widest">Inbox</span>
        </button>

        <button 
          onClick={() => typeofuser === "Customer" ? navigate("/profile") : navigate("/profile-seller")} 
          className="flex flex-col items-center gap-1 text-gray-400 hover:text-orange-600 transition-all"
        >
          <FiUser size={22} />
          <span className="text-[9px] font-black uppercase tracking-widest">Profile</span>
        </button>
      </nav>
    </div>
  );
};

export default Conversations;